export interface AdminUser {
  id: number; email: string; username: string; full_name: string | null;
  avatar_url: string | null; phone: string | null; is_active: boolean;
  is_verified: boolean; is_admin: boolean; admin_role: string | null;
  is_premium: boolean; premium_expires_at: string | null; created_at: string;
  updated_at: string; device_model: string | null; os_version: string | null;
  app_version: string | null; platform: string | null; last_active: string | null;
  language: string | null;
}
export const languages = [
  ['ar', 'Arapça', 'العربية'], ['de', 'Almanca', 'Deutsch'], ['en', 'İngilizce', 'English'],
  ['es', 'İspanyolca', 'Español'], ['fr', 'Fransızca', 'Français'], ['hi', 'Hintçe', 'हिन्दी'],
  ['id', 'Endonezce', 'Bahasa Indonesia'], ['it', 'İtalyanca', 'Italiano'], ['ja', 'Japonca', '日本語'],
  ['ko', 'Korece', '한국어'], ['nl', 'Felemenkçe', 'Nederlands'], ['pl', 'Lehçe', 'Polski'],
  ['pt', 'Portekizce', 'Português'], ['ru', 'Rusça', 'Русский'], ['tr', 'Türkçe', 'Türkçe'],
  ['vi', 'Vietnamca', 'Tiếng Việt'], ['zh-Hans', 'Çince (Basitleştirilmiş)', '简体中文'],
] as const;
export function normalizeLanguage(raw: string | null | undefined): string {
  const code = (raw || '').trim().replace(/_/g, '-').toLowerCase();
  if (/^zh(-hans)?$|^zh-(cn|sg)$|^zh-hans-/.test(code)) return 'zh-Hans';
  const base = code.split('-')[0];
  return languages.some(([key]) => key === base) ? base : 'unknown';
}
export function languageLabel(raw: string | null | undefined) {
  return languages.find(([key]) => key === normalizeLanguage(raw))?.[1] || (raw ? `Bilinmiyor · ${raw}` : 'Bilinmiyor');
}
export const platformLabel = (platform: string | null) => platform?.toLowerCase() === 'ios' ? 'iOS' : platform?.toLowerCase() === 'android' ? 'Android' : platform || 'Bilinmiyor';
export function utcTime(value: string) {
  return new Date(/[zZ]$|[+-]\d\d:\d\d$/.test(value) ? value : value + 'Z').getTime();
}
export function dateLabel(value: string | null, time = false, locale = 'tr-TR') {
  if (!value) return '—';
  const date = new Date(/[zZ]$|[+-]\d\d:\d\d$/.test(value) ? value : value + 'Z');
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric', ...(time ? { hour: '2-digit', minute: '2-digit' } : {}) }).format(date);
}
export type StatusFilter = 'all' | 'active' | 'inactive' | 'premium' | 'verified' | 'unverified' | 'admin';
export interface Filters { status: StatusFilter; language: string; version: string; platform: string; recent: boolean }
export const defaultFilters: Filters = { status: 'all', language: '', version: '', platform: '', recent: true };
export const statusLabels: Record<StatusFilter, string> = { all: 'Tüm hesaplar', active: 'Aktif hesaplar', inactive: 'Pasif hesaplar', premium: 'Premium', verified: 'Doğrulanmış', unverified: 'Doğrulanmamış', admin: 'Yöneticiler' };
export function matches(user: AdminUser, query: string, filter: Filters) {
  const text = [user.id, user.email, user.username, user.full_name, user.phone, languageLabel(user.language), user.app_version, user.device_model].join(' ').toLocaleLowerCase('tr-TR');
  const status = filter.status;
  return text.includes(query.toLocaleLowerCase('tr-TR')) &&
    (status === 'all' || (status === 'active' && user.is_active) || (status === 'inactive' && !user.is_active) ||
      (status === 'premium' && user.is_premium) || (status === 'verified' && user.is_verified) ||
      (status === 'unverified' && !user.is_verified) || (status === 'admin' && user.is_admin)) &&
    (!filter.language || normalizeLanguage(user.language) === filter.language) &&
    (!filter.version || (user.app_version || 'unknown') === filter.version) &&
    (!filter.platform || (user.platform?.toLowerCase() || 'unknown') === filter.platform) &&
    (!filter.recent || (!!user.last_active && utcTime(user.last_active) >= Date.now() - 30 * 86400000));
}
export function versionList(users: AdminUser[]) {
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  return Array.from(new Set(users.map(user => user.app_version || 'unknown'))).sort((a, b) =>
    a === 'unknown' ? 1 : b === 'unknown' ? -1 : collator.compare(b.replace(/^v/i, ''), a.replace(/^v/i, '')));
}
